import React from "react";
import { CheckCircle, XCircle, AlertTriangle } from "lucide-react";

const DimensionCards = ({ dimensions }) => {
  const getDimensionColor = (dimension) => {
    switch (dimension) {
      case "Completeness":
        return "#3b82f6";
      case "Uniqueness":
        return "#f97316";
      case "Validity":
        return "#22c55e";
      default:
        return "#3b82f6";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Excellent":
      case "Good":
        return <CheckCircle className="w-4 h-4" />;
      case "Needs Attention":
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <XCircle className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Excellent":
        return "bg-green-100 text-green-700 border-green-300";
      case "Good":
        return "bg-blue-100 text-blue-700 border-blue-300";
      case "Needs Attention":
        return "bg-orange-100 text-orange-700 border-orange-300";
      default:
        return "bg-red-100 text-red-700 border-red-300";
    }
  };

  const renderCircularGauge = (percentage, color) => {
    const radius = 80;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset =
      circumference - (percentage / 100) * circumference;

    return (
      <svg
        width="220"
        height="220"
        viewBox="0 0 200 200"
        className="mx-auto"
      >
        <circle
          cx="100"
          cy="100"
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="16"
        />
        <circle
          cx="100"
          cy="100"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="16"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform="rotate(-90 100 100)"
          style={{ transition: "stroke-dashoffset 1s ease-in-out" }}
        />
        <text
          x="100"
          y="95"
          textAnchor="middle"
          fontSize="36"
          fontWeight="700"
          fill="#0f172a"
        >
          {percentage.toFixed(1)}%
        </text>
        <text
          x="100"
          y="120"
          textAnchor="middle"
          fontSize="13"
          fill="#64748b"
        >
          Success Rate
        </text>
      </svg>
    );
  };

  if (!dimensions || dimensions.length === 0) return null;

  return (
    <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2 rounded-lg bg-blue-50">
          <svg
            className="w-5 h-5 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        </div>
        <h3 className="text-base font-semibold text-gray-900">
          Data Quality Dimensions
        </h3>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {dimensions.map((dim, idx) => {
          const color = getDimensionColor(dim.dimension);

          return (
            <div
              key={idx}
              className="rounded-xl border border-gray-200 p-4 bg-gray-50 hover:shadow-md transition"
            >
              {/* Card Header */}
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900">
                  {dim.dimension}
                </h4>
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded-full border flex items-center gap-1 ${getStatusColor(
                    dim.status
                  )}`}
                >
                  {getStatusIcon(dim.status)}
                  {dim.status}
                </span>
              </div>

              {/* Gauge */}
              <div className="mb-4">
                {renderCircularGauge(dim.percentage, color)}
              </div>

              {/* Stats */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center bg-green-50 border border-green-200 rounded-md px-3 py-2">
                  <span className="text-gray-700 font-medium">Passed</span>
                  <span className="font-bold text-green-700">
                    {dim.passed.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between items-center bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  <span className="text-gray-700 font-medium">Failed</span>
                  <span className="font-bold text-red-700">
                    {dim.failed.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between items-center bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                  <span className="text-gray-700 font-medium">Total</span>
                  <span className="font-bold text-blue-700">
                    {dim.total.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DimensionCards;
